import React, { useState, Fragment } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { PermisTypeBuilder } from './PermisTypeBuilder';
import { CreateProcessusDialog } from './CreateProcessusDialog';
import { BoldIcon, ItalicIcon, UnderlineIcon, StrikethroughIcon, LinkIcon, CodeIcon, QuoteIcon, ListIcon, ListOrderedIcon, AlignLeftIcon, PencilIcon, TrashIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon, SearchIcon, XIcon, FolderIcon, FolderTreeIcon, HardHatIcon, FileTextIcon, BadgeCheckIcon, TractorIcon, StoreIcon, FactoryIcon, LandmarkIcon, HomeIcon, WavesIcon, ConstructionIcon, ZapIcon, HammerIcon, RouteIcon, SignpostIcon, PlugIcon, ShapesIcon, BriefcaseIcon, TruckIcon, BoxIcon, SparklesIcon, CheckIcon } from 'lucide-react';
type Section = 'departements' | 'types' | 'processus' | 'messages';
interface Departement {
  id: string;
  nom: string;
  responsable: string;
}
interface ProcessStep {
  nom: string;
  couleur: string;
}
interface Processus {
  id: string;
  nom: string;
  description: string;
  etapes: ProcessStep[];
  typesAppliques: string[];
}
interface MessageTpl {
  id: string;
  titre: string;
  contenu: string;
}
// 3-level hierarchy: Catégorie → Sous-catégorie → Type de permis
interface SousCategorie {
  nom: string;
  types: string[];
}
interface Categorie {
  nom: string;
  sousCategories: SousCategorie[];
}
const PERMIT_HIERARCHY: Categorie[] = [{
  nom: 'Permis de construction',
  sousCategories: [{
    nom: 'Agricole',
    types: ['Bâtiment accessoire', 'Agrandissement', 'Modification / amélioration', 'Démolition', 'Nouvelle construction', 'Silos']
  }, {
    nom: 'Commercial',
    types: ['Modification / amélioration', 'Bâtiment accessoire', 'Agrandissement', 'Démolition', 'Nouvelle construction (grand bâtiment)', 'Nouvelle construction (petit bâtiment)', 'Aménagement locataire']
  }, {
    nom: 'Industriel',
    types: ['Bâtiment accessoire', 'Agrandissement', 'Modification / amélioration', 'Démolition', 'Nouvelle construction']
  }, {
    nom: 'Institutionnel',
    types: ['Bâtiment accessoire', 'Agrandissement', 'Modification / amélioration', 'Démolition', 'Nouvelle construction']
  }, {
    nom: 'Résidentiel',
    types: ['Logement supplémentaire', 'Immeuble en copropriété', 'Terrasse', 'Démolition', 'Garage / abri d’auto', 'Maison préfabriquée', 'Jumelé', 'Unifamiliale (SFD)', 'Agrandissement unifamiliale', 'Rénovation majeure unifamiliale', 'Rénovation mineure unifamiliale', 'Cabanon', 'Maison en rangée', 'Modèle implanté du constructeur — logement unifamilial', 'Implantation — maison en rangée']
  }, {
    nom: 'Piscines & spas',
    types: ['Piscine hors terre', 'Piscine creusée', 'Piscine semi-creusée', 'Spa', 'Swim Spa', 'Piscine temporaire']
  }, {
    nom: 'Structures désignées',
    types: ['Piscine publique extérieure', 'Spa public extérieur', 'Passerelle piétonne', 'Mur de soutènement', 'Enseignes', 'Panneaux solaires', 'Tente']
  }, {
    nom: 'Énergie & appareils',
    types: ['Poêle à granules', 'Poêle à bois', 'Appareil à combustion solide', 'Panneaux solaires', 'Éolienne']
  }, {
    nom: 'Divers construction',
    types: ['Changement d’usage (sans construction)', 'Permis conditionnel', 'Fondation', 'Lagunes', 'Permis de viabilisation du site', 'Structure temporaire', 'Transfert de permis', 'Permis de raccordement / plomberie (eau)', 'Autres permis : renouvellement, transfert, révision']
  }]
}, {
  nom: 'Autres permis',
  sousCategories: [{
    nom: 'Voirie & emprise',
    types: ['Permis d’occupation (empiètement sur l’emprise)', 'Permis d’entrée privée', 'Permis de coupe de chaussée', 'Permis de restriction de charge']
  }, {
    nom: 'Enseignes',
    types: ['Permis d’enseigne permanente', 'Permis d’enseigne murale', 'Enseignes temporaires', 'Demande de dérogation d’enseigne']
  }, {
    nom: 'Raccordements & services',
    types: ['Permis de raccordement à une borne-fontaine', 'Permis de raccordement à la conduite principale d’eau', 'Consentement municipal', 'Permis de consentement (services publics)']
  }, {
    nom: 'Divers',
    types: ['Vente de feux d’artifice', 'Permis de restriction de dimension']
  }]
}, {
  nom: 'Licences',
  sousCategories: [{
    nom: 'Affaires',
    types: ['Licence d’affaires']
  }, {
    nom: 'Véhicules de restauration',
    types: ['Classe A : à propulsion humaine', 'Classe B : aliments préparés', 'Classe B : aliments préparés (événement spécial)', 'Classe C : crème glacée / produits congelés', 'Classe C : crème glacée / produits congelés (événement spécial)', 'Classe D : cantine mobile', 'Classe D : cantine mobile (événement spécial)', 'Classe E : friterie / vendeurs sur place']
  }]
}];
const countTypes = (cat: Categorie) => cat.sousCategories.reduce((sum, sc) => sum + sc.types.length, 0);
// Maps each category / sub-category name to a semantically matching Lucide icon.
const CATEGORY_ICONS: Record<string, BoxIcon> = {
  'Permis de construction': HardHatIcon,
  'Autres permis': FileTextIcon,
  Licences: BadgeCheckIcon
};
const SUBCATEGORY_ICONS: Record<string, BoxIcon> = {
  Agricole: TractorIcon,
  Commercial: StoreIcon,
  Industriel: FactoryIcon,
  Institutionnel: LandmarkIcon,
  Résidentiel: HomeIcon,
  'Piscines & spas': WavesIcon,
  'Structures désignées': ConstructionIcon,
  'Énergie & appareils': ZapIcon,
  'Divers construction': HammerIcon,
  'Voirie & emprise': RouteIcon,
  Enseignes: SignpostIcon,
  'Raccordements & services': PlugIcon,
  Divers: ShapesIcon,
  Affaires: BriefcaseIcon,
  'Véhicules de restauration': TruckIcon
};
const getCategoryIcon = (nom: string): BoxIcon => CATEGORY_ICONS[nom] ?? FolderIcon;
const getSubCategoryIcon = (nom: string): BoxIcon => SUBCATEGORY_ICONS[nom] ?? FolderTreeIcon;
// Curated icon palette offered in the category-creation dialog.
const ICON_OPTIONS: {
  id: string;
  Icon: BoxIcon;
}[] = [{
  id: 'folder',
  Icon: FolderIcon
}, {
  id: 'hardhat',
  Icon: HardHatIcon
}, {
  id: 'file',
  Icon: FileTextIcon
}, {
  id: 'badge',
  Icon: BadgeCheckIcon
}, {
  id: 'home',
  Icon: HomeIcon
}, {
  id: 'store',
  Icon: StoreIcon
}, {
  id: 'factory',
  Icon: FactoryIcon
}, {
  id: 'landmark',
  Icon: LandmarkIcon
}, {
  id: 'tractor',
  Icon: TractorIcon
}, {
  id: 'waves',
  Icon: WavesIcon
}, {
  id: 'construction',
  Icon: ConstructionIcon
}, {
  id: 'zap',
  Icon: ZapIcon
}, {
  id: 'hammer',
  Icon: HammerIcon
}, {
  id: 'route',
  Icon: RouteIcon
}, {
  id: 'signpost',
  Icon: SignpostIcon
}, {
  id: 'plug',
  Icon: PlugIcon
}, {
  id: 'briefcase',
  Icon: BriefcaseIcon
}, {
  id: 'truck',
  Icon: TruckIcon
}, {
  id: 'shapes',
  Icon: ShapesIcon
}, {
  id: 'box',
  Icon: BoxIcon
}];
// Builds a case-insensitive regex from the query, falling back to an escaped
// literal when the query is not a valid regular expression.
const buildRegex = (query: string): RegExp => {
  try {
    return new RegExp(query, 'i');
  } catch {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, m => '\\' + m);
    return new RegExp(escaped, 'i');
  }
};
// Highlights the first regex match within a string in yellow.
const highlightRegex = (text: string, re: RegExp | null): React.ReactNode => {
  if (!re) return text;
  const match = text.match(re);
  if (!match || match.index === undefined || match[0] === '') return text;
  const start = match.index;
  const end = start + match[0].length;
  return <>
      {text.slice(0, start)}
      <mark className="bg-yellow-200 text-slate-900 rounded-sm" data-id="element-4506" data-mp="ehrobyvymu1h">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>;
};
// External request-management system that is synced with this product. The
// admin can link a permit category to one of its categories.
interface ExternalCategory {
  id: string;
  nom: string;
  systeme: string;
}
const EXTERNAL_SYSTEM_NAME = 'PSD';
const EXTERNAL_CATEGORIES: ExternalCategory[] = [{
  id: 'ext-1',
  nom: 'Construction et rénovation',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-2',
  nom: 'Permis et certificats',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-3',
  nom: 'Voirie et signalisation',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-4',
  nom: 'Enseignes et affichage',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-5',
  nom: 'Licences commerciales',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-6',
  nom: 'Arbres et espaces verts',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-7',
  nom: 'Piscines et installations',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-8',
  nom: 'Raccordements et aqueduc',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-9',
  nom: 'Réglementation municipale',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-10',
  nom: 'Véhicules de restauration',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-11',
  nom: 'Occupation du domaine public',
  systeme: EXTERNAL_SYSTEM_NAME
}, {
  id: 'ext-12',
  nom: 'Industriel et institutionnel',
  systeme: EXTERNAL_SYSTEM_NAME
}];
// Normalizes a string for fuzzy comparison (lowercase, no accents, no punctuation).
const normalizeStr = (s: string): string => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
// Token-overlap similarity score between two strings (0..1).
const similarity = (a: string, b: string): number => {
  const ta = normalizeStr(a).split(' ').filter(Boolean);
  const tb = normalizeStr(b).split(' ').filter(Boolean);
  if (ta.length === 0 || tb.length === 0) return 0;
  const setB = new Set(tb);
  const shared = ta.filter(tok => setB.has(tok)).length;
  // Also reward partial substring matches between the full strings.
  const na = normalizeStr(a);
  const nb = normalizeStr(b);
  const substr = na.includes(nb) || nb.includes(na) ? 0.4 : 0;
  return Math.min(1, shared / Math.max(ta.length, tb.length) + substr);
};
// Best external-category suggestion for a given title (null if too weak).
const getExternalSuggestion = (title: string): ExternalCategory | null => {
  const t = title.trim();
  if (t.length < 2) return null;
  let best: {
    cat: ExternalCategory;
    score: number;
  } | null = null;
  for (const cat of EXTERNAL_CATEGORIES) {
    const score = similarity(t, cat.nom);
    if (!best || score > best.score) best = {
      cat,
      score
    };
  }
  return best && best.score >= 0.34 ? best.cat : null;
};
const MOCK_DEPARTEMENTS: Departement[] = [{
  id: '1',
  nom: 'Urbanisme',
  responsable: 'Marie Tremblay'
}, {
  id: '2',
  nom: 'Permis et inspections',
  responsable: 'Jacques Dubois'
}];
const MOCK_PROCESSUS: Processus[] = [{
  id: '1',
  nom: 'Permis de construction (standard)',
  description: 'Processus complet avec analyse réglementaire et approbation du comité.',
  etapes: [{
    nom: 'Nouvelle demande',
    couleur: 'bg-slate-300'
  }, {
    nom: 'Vérification des documents',
    couleur: 'bg-amber-500'
  }, {
    nom: 'Analyse réglementaire',
    couleur: 'bg-[#4F46E5]'
  }, {
    nom: 'Approbation PIIA (CCU)',
    couleur: 'bg-purple-500'
  }, {
    nom: 'Approuvé',
    couleur: 'bg-emerald-500'
  }, {
    nom: 'Émis',
    couleur: 'bg-blue-500'
  }],
  typesAppliques: ['Résidentiel — Unifamiliale (SFD)', 'Résidentiel — Agrandissement', 'Commercial — Nouvelle construction', 'Industriel — Nouvelle construction']
}, {
  id: '2',
  nom: 'Processus simplifié',
  description: 'Traitement accéléré sans passage en comité.',
  etapes: [{
    nom: 'Nouvelle demande',
    couleur: 'bg-slate-300'
  }, {
    nom: 'Vérification',
    couleur: 'bg-amber-500'
  }, {
    nom: 'Approuvé',
    couleur: 'bg-emerald-500'
  }, {
    nom: 'Émis',
    couleur: 'bg-blue-500'
  }],
  typesAppliques: ['Résidentiel — Cabanon', 'Résidentiel — Terrasse', 'Piscine hors terre', 'Abattage d’arbre']
}, {
  id: '3',
  nom: 'Licence / occupation',
  description: 'Processus dédié aux licences et permis d’occupation.',
  etapes: [{
    nom: 'Demande reçue',
    couleur: 'bg-slate-300'
  }, {
    nom: 'Validation',
    couleur: 'bg-[#4F46E5]'
  }, {
    nom: 'Délivrée',
    couleur: 'bg-emerald-500'
  }],
  typesAppliques: ['Licence d’affaires', 'Permis d’occupation (empiètement sur l’emprise)']
}];
const MOCK_MESSAGES: MessageTpl[] = [{
  id: '1',
  titre: 'Confirmation de réception',
  contenu: 'Votre demande de permis a bien été reçue et sera analysée dans les meilleurs délais.'
}, {
  id: '2',
  titre: 'Permis émis',
  contenu: 'Nous vous informons que votre permis a été émis. Vous pouvez le récupérer sur le portail citoyen.'
}];
export function PermisConfigurer() {
  const {
    t
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>('types');
  const [departements] = useState<Departement[]>(MOCK_DEPARTEMENTS);
  const [processus] = useState<Processus[]>(MOCK_PROCESSUS);
  const [messages, setMessages] = useState<MessageTpl[]>(MOCK_MESSAGES);
  // Full-screen builder state
  const [editingType, setEditingType] = useState<{
    categorie: string;
    sousCategorie: string;
    type: string;
  } | null>(null);
  // Expanded state per category and sub-category (keyed by name)
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({
    'Permis de construction': true
  });
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({});
  const [typeSearch, setTypeSearch] = useState('');
  // Category-creation / edition dialog. The same dialog is reused for adding a
  // category, editing a category, and editing a sub-category — only prefilled.
  const [showAddCat, setShowAddCat] = useState(false);
  const [catDialogMode, setCatDialogMode] = useState<'create' | 'edit-cat' | 'edit-sub'>('create');
  const [dialogLang, setDialogLang] = useState<'fr' | 'en'>('fr');
  const [newCat, setNewCat] = useState({
    titreFr: '',
    titreEn: '',
    descFr: '',
    descEn: '',
    iconId: 'folder'
  });
  const [showCreateProcessus, setShowCreateProcessus] = useState(false);
  // External-system category link state for the category/sub-category dialog.
  const [linkedExternal, setLinkedExternal] = useState<ExternalCategory | null>(null);
  const [externalSearch, setExternalSearch] = useState('');
  const [showExternalResults, setShowExternalResults] = useState(false);
  const resetNewCat = () => setNewCat({
    titreFr: '',
    titreEn: '',
    descFr: '',
    descEn: '',
    iconId: 'folder'
  });
  // Best-effort: resolve the palette icon id that corresponds to a category or
  // sub-category name, so the edit dialog shows the same icon currently in use.
  const resolveIconId = (Icon: BoxIcon): string => ICON_OPTIONS.find(opt => opt.Icon === Icon)?.id ?? 'folder';
  // Opens the dialog in "add category" mode (empty form).
  const openAddCat = () => {
    setCatDialogMode('create');
    resetNewCat();
    setLinkedExternal(null);
    setExternalSearch('');
    setShowExternalResults(false);
    setDialogLang('fr');
    setShowAddCat(true);
  };
  // Opens the dialog in "edit category" mode, prefilled with the category data.
  const openEditCat = (cat: Categorie) => {
    setCatDialogMode('edit-cat');
    setNewCat({
      titreFr: cat.nom,
      titreEn: '',
      descFr: '',
      descEn: '',
      iconId: resolveIconId(getCategoryIcon(cat.nom))
    });
    setLinkedExternal(null);
    setExternalSearch('');
    setShowExternalResults(false);
    setDialogLang('fr');
    setShowAddCat(true);
  };
  // Opens the dialog in "edit sub-category" mode, prefilled with the data.
  const openEditSub = (sub: SousCategorie) => {
    setCatDialogMode('edit-sub');
    setNewCat({
      titreFr: sub.nom,
      titreEn: '',
      descFr: '',
      descEn: '',
      iconId: resolveIconId(getSubCategoryIcon(sub.nom))
    });
    setLinkedExternal(null);
    setExternalSearch('');
    setShowExternalResults(false);
    setDialogLang('fr');
    setShowAddCat(true);
  };
  const closeAddCat = () => {
    setShowAddCat(false);
    setCatDialogMode('create');
    setDialogLang('fr');
    setLinkedExternal(null);
    setExternalSearch('');
    setShowExternalResults(false);
    resetNewCat();
  };
  const submitNewCat = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app this would persist the created / edited category.
    closeAddCat();
  };
  const toggleCat = (name: string) => setOpenCats(prev => ({
    ...prev,
    [name]: !prev[name]
  }));
  const toggleSub = (key: string) => setOpenSubs(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  const renderToolbar = () => <div className="flex items-center gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-lg flex-wrap" data-id="element-4507" data-mp="3ryacsedj10xz">
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white" data-id="element-4508" data-mp="rqr2465jqkw">
        <option data-id="element-4509" data-mp="1q6x91snaoz3i">Sans Serif</option>
      </select>
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white ml-2" data-id="element-4510" data-mp="1q41aq37jc8ej">
        <option data-id="element-4511" data-mp="1v2w2hunhd7as">Normal</option>
      </select>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-4512" data-mp="258dqokqlcwsm">
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4513" data-mp="20a2linb8ragq">
          <BoldIcon className="w-4 h-4" data-id="element-4514" data-mp="20bne47dzwjby" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4515" data-mp="2e1h1el9uqvjk">
          <ItalicIcon className="w-4 h-4" data-id="element-4516" data-mp="2e31vk9s98cgw" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4517" data-mp="299pdkii2z3x4">
          <UnderlineIcon className="w-4 h-4" data-id="element-4518" data-mp="2nyvjsqfc5uei" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4519" data-mp="2o1hp0jer7xtk">
          <StrikethroughIcon className="w-4 h-4" data-id="element-4520" data-mp="2iz8x7qdmb1t1" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-4521" data-mp="2x0la1sxhenap">
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4522" data-mp="2syrd9vqeq3ft">
          <LinkIcon className="w-4 h-4" data-id="element-4523" data-mp="2sz9g5b7krfwp" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4524" data-mp="12laccgn98o2x">
          <CodeIcon className="w-4 h-4" data-id="element-4525" data-mp="12nvfd8zaha29" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4526" data-mp="yls1kdp5dtdt">
          <QuoteIcon className="w-4 h-4" data-id="element-4527" data-mp="5y8kayrtaavo" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-4528" data-mp="5wf9r8e2v6ie">
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4529" data-mp="z5db8s9tul2">
          <ListIcon className="w-4 h-4" data-id="element-4530" data-mp="fol3amjz9vzp" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4531" data-mp="fq4gu5e91sxs">
          <ListOrderedIcon className="w-4 h-4" data-id="element-4532" data-mp="aqji9w2zrmll" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" data-id="element-4533" data-mp="3j4icotabezwz">
          <AlignLeftIcon className="w-4 h-4" data-id="element-4534" data-mp="3j74lsxcutnhb" />
        </button>
      </div>
    </div>;
  const renderDepartements = () => <div className="space-y-4" data-id="element-4535" data-mp="3s5q7v2hjbkmu">
      <div className="flex items-center justify-between" data-id="element-4536" data-mp="3s6rm1ftukn8s">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4537" data-mp="3o44qb3u22i6b">
          {t('Départements', 'Departments')}
        </h3>
        <button className="px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2" data-id="element-4538" data-mp="17ey7yy66wqm5">
          <PlusIcon className="w-4 h-4" data-id="element-4539" data-mp="1axeyr7jxp829" />{' '}
          {t('Ajouter un département', 'Add a department')}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-4540" data-mp="1fxjc4opjpmo9">
        <table className="w-full" data-id="element-4541" data-mp="1100r9xfzntm3">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-4542" data-mp="110scqffvgayn">
            <tr data-id="element-4543" data-mp="15xit73vl98kl">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-4544" data-mp="15z3m6p298jgc">
                {t('Nom', 'Name')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-4545" data-mp="1yniyv4xwypoi">
                {t('Responsable', 'Manager')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide w-24" data-id="element-4546" data-mp="1ox6g9i9nt6l0">
                {t('Actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4547" data-mp="2hd7fru4xk5kv">
            {departements.map(dept => <tr key={dept.id} className="hover:bg-slate-50" data-id="element-4548" data-mp="2r97ka6oan3nd">
                <td className="px-4 py-3 text-sm text-slate-700 font-medium" data-id="element-4549" data-mp="2r7d9vmanetxj">
                  {dept.nom}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600" data-id="element-4550" data-mp="2i8j69bywh3ah">
                  {dept.responsable}
                </td>
                <td className="px-4 py-3 text-center" data-id="element-4551" data-mp="2dahmspxkb1yh">
                  <div className="flex items-center justify-center gap-2" data-id="element-4552" data-mp="2dc13ghn4rf0s">
                    <button className="text-slate-400 hover:text-[#4F46E5]" data-id="element-4553" data-mp="28bwm6peeeg9i">
                      <PencilIcon className="w-4 h-4" data-id="element-4554" data-mp="23itzn181g72t" />
                    </button>
                    <button className="text-slate-400 hover:text-red-600" data-id="element-4555" data-mp="1yk0ovgk4yu6c">
                      <TrashIcon className="w-4 h-4" data-id="element-4556" data-mp="1ujgqk9nttkim" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderTypes = () => {
    const query = typeSearch.trim();
    const isSearching = query.length > 0;
    const re = isSearching ? buildRegex(query) : null;
    // Filter the hierarchy down to branches with a regex match. A parent whose
    // own name matches keeps all of its children.
    const displayed = PERMIT_HIERARCHY.map(cat => {
      const catMatch = !isSearching || re!.test(cat.nom);
      const subs = catMatch ? cat.sousCategories : cat.sousCategories.map(sub => {
        const subMatch = re!.test(sub.nom);
        const types = subMatch ? sub.types : sub.types.filter(t => re!.test(t));
        return {
          ...sub,
          types
        };
      }).filter(sub => re!.test(sub.nom) || sub.types.length > 0);
      return {
        ...cat,
        sousCategories: subs
      };
    }).filter(cat => !isSearching || re!.test(cat.nom) || cat.sousCategories.length > 0);
    return <div className="space-y-4" data-id="element-4557" data-mp="2yqvz4aahjjek">
        <div data-id="element-4558" data-mp="2fo3vrnrfrkmj">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4559" data-mp="2fnkfsclcj6b5">
            {t('Types de Permis', 'Permit Types')}
          </h3>
        </div>

        {/* Search bar */}
        <div className="relative" data-id="element-4560" data-mp="2pe6nlkmtmsja">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4561" data-mp="2pef4ropz8k0a" />
          <input type="text" value={typeSearch} onChange={e => setTypeSearch(e.target.value)} placeholder={t('Rechercher un type de permis...', 'Search permit types...')} className="w-full pl-9 pr-9 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4562" data-mp="2kmopd4c6n620" />
          {isSearching && <button onClick={() => setTypeSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100 transition-colors" aria-label={t('Effacer la recherche', 'Clear search')} data-id="element-4563" data-mp="1dy8b16sfpodb">
              <XIcon className="w-4 h-4" data-id="element-4564" data-mp="3plbqk9plo30w" />
            </button>}
        </div>

        {isSearching && displayed.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-12 px-6 text-center" data-id="element-4565" data-mp="3avcviftcd7pn">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-4566" data-mp="3fuh5h8o3b9h1">
              <SearchIcon className="w-5 h-5 text-slate-400" data-id="element-4567" data-mp="31z5ad060vgzl" />
            </div>
            <p className="text-sm font-medium text-slate-600" data-id="element-4568" data-mp="35zp8o72c0qnb">
              {t(`Aucun type de permis ne correspond à « ${query} ».`, `No permit type matches "${query}".`)}
            </p>
            <p className="text-xs text-slate-400 mt-1" data-id="element-4569" data-mp="q7hvczdbghsl">
              {t('Essayez un autre terme ou créez un nouveau type.', 'Try another term or create a new type.')}
            </p>
            <button className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-4570" data-mp="tpxevtotkrm4">
              <PlusIcon className="w-4 h-4" data-id="element-4571" data-mp="jqgbxk0ycrtu" />{' '}
              {t('Créer un type', 'Create a type')}
            </button>
          </div> : <div className="space-y-3" data-id="element-4572" data-mp="etpyz5kg2ccn">
            {!isSearching && <button onClick={openAddCat} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA] transition-colors" data-id="element-4573" data-mp="1cec54erbc1lv">
                <PlusIcon className="w-4 h-4" data-id="element-4574" data-mp="12o9dahvxgtp4" />{' '}
                {t('Ajouter une catégorie', 'Add a category')}
              </button>}
            {displayed.map(cat => {
          const catOpen = isSearching ? true : openCats[cat.nom] ?? false;
          const CatIcon = getCategoryIcon(cat.nom);
          return <div key={cat.nom} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-4575" data-mp="1yoc7hjd5d5fm">
                  {/* Level 1 — Catégorie */}
                  <div className="group/cat flex items-center gap-2 pr-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-id="element-4576" data-mp="27o7ldvy37ejc">
                    <button onClick={() => toggleCat(cat.nom)} className="min-w-0 flex items-center gap-3 pl-4 pr-1.5 py-3 text-left" data-id="element-4577" data-mp="2comycmegik0s">
                      {catOpen ? <ChevronDownIcon className="w-4 h-4 text-slate-500 flex-shrink-0" data-id="element-4578" data-mp="2lmfhk1naubtg" /> : <ChevronRightIcon className="w-4 h-4 text-slate-500 flex-shrink-0" data-id="element-4579" data-mp="2ve1ymembj544" />}
                      <CatIcon className="w-4 h-4 text-[#4F46E5] flex-shrink-0" data-id="element-4580" data-mp="30dy1gaja4qi3" />
                      <span className="text-sm font-bold text-[#0F172A] truncate" data-id="element-4581" data-mp="35c9d8qt4xb2y">
                        {isSearching ? highlightRegex(cat.nom, re) : cat.nom}
                      </span>
                    </button>
                    {!isSearching && <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover/cat:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4582" data-mp="3lo3cd2f5wle3">
                        <button className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:text-[#4338CA] px-2 py-1 rounded" data-id="element-4583" data-mp="3uot6m1ory3pp">
                          <PlusIcon className="w-3.5 h-3.5" data-id="element-4584" data-mp="36xe0ryxsh49p" /> Sous-catégorie
                        </button>
                        <button onClick={() => openEditCat(cat)} className="p-1.5 rounded text-slate-400 hover:text-[#4F46E5] hover:bg-slate-200/70 transition-colors" aria-label={`Modifier ${cat.nom}`} data-id="element-4585" data-mp="32w263f025sun">
                          <PencilIcon className="w-4 h-4" data-id="element-4586" data-mp="2j9w0ff0gkxv4" />
                        </button>
                        <button className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-slate-200/70 transition-colors" aria-label={`Supprimer ${cat.nom}`} data-id="element-4587" data-mp="2j81p8sezok48">
                          <TrashIcon className="w-4 h-4" data-id="element-4588" data-mp="ps870rj0mf7n" />
                        </button>
                      </div>}
                  </div>

                  {catOpen && <div className="divide-y divide-slate-100" data-id="element-4589" data-mp="1igvyj4ucy0tn">
                      {cat.sousCategories.map(sub => {
                const subKey = `${cat.nom}::${sub.nom}`;
                const subOpen = isSearching ? true : openSubs[subKey] ?? false;
                const SubIcon = getSubCategoryIcon(sub.nom);
                return <div key={subKey} data-id="element-4590" data-mp="3t6kq5ku2mmcv">
                            {/* Level 2 — Sous-catégorie */}
                            <div className="group/sub flex items-center gap-2 pr-3 hover:bg-slate-50 transition-colors" data-id="element-4591" data-mp="1zhzqvm24k3p">
                              <button onClick={() => toggleSub(subKey)} className="min-w-0 flex items-center gap-3 pl-8 pr-1 py-2.5 text-left" data-id="element-4592" data-mp="fhjcryr7hgqu">
                                {subOpen ? <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4593" data-mp="pdcya7mwkkkr" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4594" data-mp="3sxhdadlnm52v" />}
                                <SubIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4595" data-mp="1o1usie64c2x" />
                                <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-4596" data-mp="6i65ncqk149a">
                                  {isSearching ? highlightRegex(sub.nom, re) : sub.nom}
                                </span>
                                <span className="text-xs text-slate-400 tabular-nums flex-shrink-0" data-id="element-4597" data-mp="1ra0tly45j81d">
                                  {sub.types.length}
                                </span>
                              </button>
                              {!isSearching && <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover/sub:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4598" data-mp="1da9choic9bj5">
                                  <button className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:text-[#4338CA] px-2 py-1 rounded" data-id="element-4599" data-mp="13kzghxkdj8pr">
                                    <PlusIcon className="w-3.5 h-3.5" data-id="element-4600" data-mp="zh9thcjckz6u" /> Type
                                  </button>
                                  <button onClick={() => openEditSub(sub)} className="p-1.5 rounded text-slate-400 hover:text-[#4F46E5] hover:bg-slate-200/70 transition-colors" aria-label={`Modifier ${sub.nom}`} data-id="element-4601" data-mp="pl6swb4598m0">
                                    <PencilIcon className="w-4 h-4" data-id="element-4602" data-mp="fz5l6x5y3gaw" />
                                  </button>
                                  <button className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-slate-200/70 transition-colors" aria-label={`Supprimer ${sub.nom}`} data-id="element-4603" data-mp="205z9x9thfif">
                                    <TrashIcon className="w-4 h-4" data-id="element-4604" data-mp="ynkbfi8fuqan" />
                                  </button>
                                </div>}
                            </div>

                            {/* Level 3 — Types de permis */}
                            {subOpen && <ul className="pb-2" data-id="element-4605" data-mp="1cma9b0330u02">
                                {sub.types.map(type => <li key={`${subKey}::${type}`} className="flex items-center gap-3 pl-14 pr-4 py-2 hover:bg-slate-50 group/type" data-id="element-4606" data-mp="3258ppw3efx4f">
                                    <span className="text-sm text-slate-600" data-id="element-4607" data-mp="3buimhp96u3yv">
                                      {isSearching ? highlightRegex(type, re) : type}
                                    </span>
                                    <div className="flex items-center gap-1 opacity-0 group-hover/type:opacity-100 transition-opacity" data-id="element-4608" data-mp="2e8l7w2xxiis7">
                                      <button onClick={() => setEditingType({
                          categorie: cat.nom,
                          sousCategorie: sub.nom,
                          type: type
                        })} className="text-slate-400 hover:text-[#4F46E5]" aria-label={`Modifier ${type}`} data-id="element-4609" data-mp="2t27mc35x4k4g">
                                        <PencilIcon className="w-4 h-4" data-id="element-4610" data-mp="2n3tkq6htuffm" />
                                      </button>
                                      <button className="text-slate-400 hover:text-red-600" aria-label={`Supprimer ${type}`} data-id="element-4611" data-mp="2s1brbq8dgikg">
                                        <TrashIcon className="w-4 h-4" data-id="element-4612" data-mp="31ukbrcblhaho" />
                                      </button>
                                    </div>
                                  </li>)}
                              </ul>}
                          </div>;
              })}
                    </div>}
                </div>;
        })}
          </div>}

        {/* Add-category dialog */}
        {showAddCat && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="add-cat-title" data-id="element-4613" data-mp="3fyhwawwofkpl">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" onClick={closeAddCat} data-id="element-4614" data-mp="cghw43xd4jmj" />
            <form onSubmit={submitNewCat} className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl max-h-[90vh] flex flex-col" data-id="element-4615" data-mp="2r6oymu1dp1a">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200" data-id="element-4616" data-mp="v8j2rsgdpe6i">
                <h3 id="add-cat-title" className="text-base font-bold text-[#0F172A]" data-id="element-4617" data-mp="qfpx92d361td">
                  {catDialogMode === 'edit-cat' ? t('Modifier la catégorie', 'Edit category') : catDialogMode === 'edit-sub' ? t('Modifier la sous-catégorie', 'Edit sub-category') : t('Ajouter une catégorie', 'Add a category')}
                </h3>
                <div className="flex items-center gap-3" data-id="element-4618" data-mp="21yeugbi8xzzo">
                  {/* Language toggle — switch the language of the content fields */}
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label={t('Langue du contenu', 'Content language')} data-id="element-4619" data-mp="26yszrlsgqvu0">
                    {(['fr', 'en'] as const).map(lang => <button key={lang} type="button" onClick={() => setDialogLang(lang)} aria-pressed={dialogLang === lang} className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${dialogLang === lang ? 'bg-[#0F172A] text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-4620" data-mp="3merwm0wmr5ey">
                        {lang}
                      </button>)}
                  </div>
                  <button type="button" onClick={closeAddCat} className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label={t('Fermer', 'Close')} data-id="element-4621" data-mp="1yeweyuukcxjd">
                    <XIcon className="w-5 h-5" data-id="element-4622" data-mp="23ftxx9nedvlx" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5 overflow-y-auto" data-id="element-4623" data-mp="919ipchuis9x">
                {/* Titre */}
                <div data-id="element-4624" data-mp="3jdht3meu6kuh">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-4625" data-mp="3je9pv0k66omc">
                    {t('Titre', 'Title')}{' '}
                    <span className="text-xs font-medium text-slate-400" data-id="element-4626" data-mp="3ob05xnvz5k7r">
                      (
                      {dialogLang === 'fr' ? t('Français', 'French') : t('Anglais', 'English')}
                      )
                    </span>
                  </label>
                  <input type="text" value={dialogLang === 'fr' ? newCat.titreFr : newCat.titreEn} onChange={e => setNewCat(p => ({
                ...p,
                [dialogLang === 'fr' ? 'titreFr' : 'titreEn']: e.target.value
              }))} placeholder={dialogLang === 'fr' ? 'Permis de construction' : 'Building permit'} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4627" data-mp="30gqg1upw11yv" />
                </div>

                {/* Description */}
                <div data-id="element-4628" data-mp="20cpn4jn99hfu">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-4629" data-mp="1vfquk5j8pijr">
                    {t('Description', 'Description')}{' '}
                    <span className="text-xs font-medium text-slate-400" data-id="element-4630" data-mp="1rdcbbkbnsgry">
                      (
                      {dialogLang === 'fr' ? t('Français', 'French') : t('Anglais', 'English')}
                      )
                    </span>
                  </label>
                  <textarea value={dialogLang === 'fr' ? newCat.descFr : newCat.descEn} onChange={e => setNewCat(p => ({
                ...p,
                [dialogLang === 'fr' ? 'descFr' : 'descEn']: e.target.value
              }))} rows={3} placeholder={dialogLang === 'fr' ? 'Description en français…' : 'Description in English…'} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4631" data-mp="3gsadp95ao5vo" />
                </div>

                {/* Icône */}
                <div data-id="element-4632" data-mp="7pcmamqmguji">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-4633" data-mp="7o1kgsgqlwv1">
                    {t('Icône', 'Icon')}
                  </label>
                  <div className="grid grid-cols-8 gap-2" data-id="element-4634" data-mp="10di719hoejjc">
                    {ICON_OPTIONS.map(({
                  id,
                  Icon
                }) => {
                  const selected = newCat.iconId === id;
                  return <button key={id} type="button" onClick={() => setNewCat(p => ({
                    ...p,
                    iconId: id
                  }))} aria-pressed={selected} className={`aspect-square flex items-center justify-center rounded-lg border transition-colors ${selected ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-4635" data-mp="1cmy6z8498cta">
                          <Icon className="w-4 h-4" data-id="element-4636" data-mp="29dqn25r6u4v2" />
                        </button>;
                })}
                  </div>
                </div>

                {/* External system link */}
                {(() => {
              const suggestion = getExternalSuggestion(newCat.titreFr);
              const showSuggestion = !linkedExternal && suggestion !== null && externalSearch.trim().length === 0;
              const results = externalSearch.trim() ? EXTERNAL_CATEGORIES.filter(c => normalizeStr(c.nom).includes(normalizeStr(externalSearch))) : EXTERNAL_CATEGORIES;
              return <div className="pt-1" data-id="element-4637" data-mp="16y6heymfgr15">
                      <label className="block text-sm font-semibold text-slate-700 mb-1" data-id="element-4638" data-mp="16wvibc3xg7gd">
                        {t('Catégorie système externe', 'External system category')}
                      </label>
                      <p className="text-xs text-slate-400 mb-2" data-id="element-4639" data-mp="33tciq1jh1zif">
                        {t(`Liez cette catégorie à une catégorie de ${EXTERNAL_SYSTEM_NAME}`, `Link this category to a ${EXTERNAL_SYSTEM_NAME} category`)}
                      </p>

                      {linkedExternal ? <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50" data-id="element-4640" data-mp="1ehfub1u577uy">
                          <div className="flex items-center gap-2.5 min-w-0" data-id="element-4641" data-mp="10lc2tganldm6">
                            <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0" data-id="element-4642" data-mp="15jdng5nq9kzr">
                              <LinkIcon className="w-4 h-4" data-id="element-4643" data-mp="vjcxw1z9m8n4" />
                            </div>
                            <div className="min-w-0" data-id="element-4644" data-mp="2lwlpfz5i70tk">
                              <div className="text-sm font-semibold text-slate-900 truncate" data-id="element-4645" data-mp="2lx3pz7z7817a">
                                {linkedExternal.nom}
                              </div>
                              <div className="text-[11px] text-slate-500 truncate" data-id="element-4646" data-mp="215yg4k9zzlk3">
                                {linkedExternal.systeme} · {t('Lié', 'Linked')}
                              </div>
                            </div>
                          </div>
                          <button type="button" onClick={() => {
                    setLinkedExternal(null);
                    setExternalSearch('');
                  }} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-white transition-colors flex-shrink-0" aria-label={t('Retirer le lien', 'Remove link')} data-id="element-4647" data-mp="1mailoe5i9nn6">
                            <XIcon className="w-4 h-4" data-id="element-4648" data-mp="ykxytk68e68m" />
                          </button>
                        </div> : <>
                          {showSuggestion && <button type="button" onClick={() => {
                    setLinkedExternal(suggestion);
                    setShowExternalResults(false);
                  }} className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-2 rounded-lg border border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50 transition-colors text-left" data-id="element-4649" data-mp="365enopigp60l">
                              <div className="w-7 h-7 rounded-md bg-[#4F46E5] text-white flex items-center justify-center flex-shrink-0" data-id="element-4650" data-mp="2rhtjstdrj2rm">
                                <SparklesIcon className="w-4 h-4" data-id="element-4651" data-mp="3173bw98lwlfq" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-4652" data-mp="23rpfskwnx103">
                                <div className="text-[11px] font-bold uppercase tracking-wide text-[#4F46E5]" data-id="element-4653" data-mp="23rfpew7pgrtv">
                                  {t('Suggestion', 'Suggestion')}
                                </div>
                                <div className="text-sm font-semibold text-slate-900 truncate" data-id="element-4654" data-mp="20o040swo1nyh">
                                  {suggestion!.nom}
                                </div>
                              </div>
                              <span className="text-xs font-semibold text-[#4F46E5] flex items-center gap-1 flex-shrink-0" data-id="element-4655" data-mp="25gtcnrv99gfu">
                                <CheckIcon className="w-3.5 h-3.5" data-id="element-4656" data-mp="1hqgu7n2dw1b0" />
                                {t('Lier', 'Link')}
                              </span>
                            </button>}

                          <div className="relative" data-id="element-4657" data-mp="1rq7njlf7ka9i">
                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4658" data-mp="1mqlapd3akw18" />
                            <input type="text" value={externalSearch} onChange={e => {
                      setExternalSearch(e.target.value);
                      setShowExternalResults(true);
                    }} onFocus={() => setShowExternalResults(true)} placeholder={t('Rechercher une catégorie externe…', 'Search an external category…')} className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4659" data-mp="333a4wip4t3uq" />
                            {showExternalResults && <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-4660" data-mp="ytp169iswt96">
                                {results.length > 0 ? results.map(c => <li key={c.id} data-id="element-4661" data-mp="3nexqb8chn7yv">
                                      <button type="button" onClick={() => {
                          setLinkedExternal(c);
                          setShowExternalResults(false);
                          setExternalSearch('');
                        }} className="w-full flex items-center gap-2.5 text-left px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50" data-id="element-4662" data-mp="3nfqq1pd14x7i">
                                        <LinkIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4663" data-mp="8535o0vti0v" />
                                        <span className="truncate" data-id="element-4664" data-mp="3sd8zdguyleg1">
                                          {c.nom}
                                        </span>
                                      </button>
                                    </li>) : <li className="px-3 py-2 text-sm text-slate-400" data-id="element-4665" data-mp="wehwhiqvmllo">
                                    {t('Aucun résultat', 'No results')}
                                  </li>}
                              </ul>}
                          </div>
                        </>}
                    </div>;
            })()}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200" data-id="element-4666" data-mp="1k802gycqauk4">
                <button type="button" onClick={closeAddCat} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4667" data-mp="1o5e7edtdmg6b">
                  {t('Annuler', 'Cancel')}
                </button>
                <button type="submit" disabled={!newCat.titreFr.trim()} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4668" data-mp="3gk09w9q6mz69">
                  {catDialogMode === 'create' ? t('Créer la catégorie', 'Create category') : t('Enregistrer', 'Save')}
                </button>
              </div>
            </form>
          </div>}
      </div>;
  };
  const renderProcessus = () => <div className="space-y-4" data-id="element-4669" data-mp="3qa31c5hno52h">
      <div className="flex items-center justify-between" data-id="element-4670" data-mp="3q8ryq8zy53cy">
        <div data-id="element-4671" data-mp="3uavcx5e02m1x">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4672" data-mp="3uadimeawehwn">
            {t('Processus', 'Workflows')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-4673" data-mp="2etbrdha79nr1">
            {t('Définissez les étapes (statuts) d’un processus, puis appliquez-le à plusieurs types de permis.', 'Define workflow steps (statuses), then apply them to multiple permit types.')}
          </p>
        </div>
        <button onClick={() => setShowCreateProcessus(true)} className="px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-4674" data-mp="26cuhri2usecj">
          <PlusIcon className="w-4 h-4" data-id="element-4675" data-mp="1sn0s4xjixq1r" />{' '}
          {t('Nouveau processus', 'New workflow')}
        </button>
      </div>

      <CreateProcessusDialog open={showCreateProcessus} onClose={() => setShowCreateProcessus(false)} data-id="element-4676" data-mp="1nopjgq4ytll4" />

      <div className="space-y-4" data-id="element-4677" data-mp="1xl2d5o0ifzfv">
        {processus.map(proc => <div key={proc.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-4678" data-mp="1xjraxsmpqzqv">
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 bg-slate-50 border-b border-slate-200" data-id="element-4679" data-mp="14vbxvbn56ri6">
              <div data-id="element-4680" data-mp="1ujr5c29vyp4a">
                <div className="flex items-center gap-2" data-id="element-4681" data-mp="1ukacqp1wtv42">
                  <h4 className="text-sm font-bold text-[#0F172A]" data-id="element-4682" data-mp="1ylcbz5vtu761">
                    {proc.nom}
                  </h4>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500" data-id="element-4683" data-mp="23ddwtg72967o">
                    {proc.etapes.length} {t('étapes', 'steps')}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1" data-id="element-4684" data-mp="1fn9tzb1p5ahq">
                  {proc.description}
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-4685" data-mp="1klbbvsndz3rm">
                <button className="text-slate-400 hover:text-[#4F46E5]" data-id="element-4686" data-mp="1kopcavchp2wf">
                  <PencilIcon className="w-4 h-4" data-id="element-4687" data-mp="1pmqxblth7caj" />
                </button>
                <button className="text-slate-400 hover:text-red-600" data-id="element-4688" data-mp="2w0q7ot3uzpl5">
                  <TrashIcon className="w-4 h-4" data-id="element-4689" data-mp="31034o6og2d1g" />
                </button>
              </div>
            </div>

            {/* Steps */}
            <div className="px-5 py-4" data-id="element-4690" data-mp="pcpwf8eund4h">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3" data-id="element-4691" data-mp="ke66kwx4xh6u">
                {t('Étapes du processus', 'Workflow steps')}
              </div>
              <div className="flex flex-wrap items-center gap-2" data-id="element-4692" data-mp="fld3edhbegwv">
                {proc.etapes.map((etape, i) => <Fragment key={etape.nom} data-id="element-4693" data-mp="amijf87m2mb6" data-mpi={i}>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white" data-id="element-4694" data-mp="6nsy8tgc00gz">
                      <span className="text-[10px] font-bold text-slate-400 w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-4695" data-mp="1m3qbmoeo2x4">
                        {i + 1}
                      </span>
                      <span className={`w-2.5 h-2.5 rounded-full ${etape.couleur}`} data-id="element-4696" data-mp="3nwpp37q0h8og"></span>
                      <span className="text-sm font-medium text-slate-700" data-id="element-4697" data-mp="1m3rub6coc60v">
                        {etape.nom}
                      </span>
                    </div>
                    {i < proc.etapes.length - 1 && <ChevronRightIcon className="w-4 h-4 text-slate-300" data-id="element-4698" data-mp="1z3kiqy55f03o" />}
                  </Fragment>)}
                <button className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors text-sm font-medium" data-id="element-4699" data-mp="1k84jmeppoe0f">
                  <PlusIcon className="w-3.5 h-3.5" data-id="element-4700" data-mp="1g4gcfx4zrkfp" /> {t('Étape', 'Step')}
                </button>
              </div>
            </div>

            {/* Applied permit types */}
            <div className="px-5 py-4 border-t border-slate-100" data-id="element-4701" data-mp="11dfsauuvu23q">
              <div className="flex items-center justify-between mb-3" data-id="element-4702" data-mp="16fewg6yg531q">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wide" data-id="element-4703" data-mp="sec8tprszkop">
                  {t(`Appliqué à ${proc.typesAppliques.length} type(s) de permis`, `Applied to ${proc.typesAppliques.length} permit type(s)`)}
                </div>
                <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA] flex items-center gap-1" data-id="element-4704" data-mp="8u3xeuobt6tj">
                  <PlusIcon className="w-3.5 h-3.5" data-id="element-4705" data-mp="muep1zxgmpfr" />{' '}
                  {t('Appliquer à des types', 'Apply to types')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-4706" data-mp="wqhsra7ymw4t">
                {proc.typesAppliques.map(type => <span key={type} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100 text-xs font-medium text-[#4F46E5]" data-id="element-4707" data-mp="rspvs1sgkjtr">
                    {type}
                    <button className="text-[#4F46E5]/60 hover:text-[#4F46E5]" aria-label={`Retirer ${type}`} data-id="element-4708" data-mp="1fhimnju841hi">
                      <TrashIcon className="w-3 h-3" data-id="element-4709" data-mp="1agbehjo650ab" />
                    </button>
                  </span>)}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
  const renderMessages = () => <div className="space-y-6" data-id="element-4710" data-mp="h56a1fo0kb83">
      <div className="flex items-center justify-between" data-id="element-4711" data-mp="h4wmdyqfyg5k">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4712" data-mp="lw4wyy3ic7kb">
          Messages automatisés
        </h3>
        <button className="px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2" data-id="element-4713" data-mp="qw0xgnczxgv4">
          <PlusIcon className="w-4 h-4" data-id="element-4714" data-mp="zwfl3tdfn81s" /> Nouveau message
        </button>
      </div>

      <div className="space-y-6" data-id="element-4715" data-mp="zucup88dbcwj">
        {messages.map((msg, idx) => <div key={msg.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-4716" data-mp="14uhep88hipri" data-mpi={idx}>
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between" data-id="element-4717" data-mp="36mdpgfmtvvl8">
              <input type="text" value={msg.titre} onChange={e => {
            const updated = [...messages];
            updated[idx].titre = e.target.value;
            setMessages(updated);
          }} className="font-semibold text-slate-700 bg-transparent border-none focus:outline-none focus:ring-0 w-full" data-id="element-4718" data-mp="2z3mo0gacjzjx" />
              <button className="text-slate-400 hover:text-red-600 ml-4" data-id="element-4719" data-mp="3i0xlpwgbykx9">
                <TrashIcon className="w-4 h-4" data-id="element-4720" data-mp="3d2dwnn6fwt0o" />
              </button>
            </div>
            <div className="p-4" data-id="element-4721" data-mp="383sxzs6u6hec">
              {renderToolbar()}
              <textarea value={msg.contenu} onChange={e => {
            const updated = [...messages];
            updated[idx].contenu = e.target.value;
            setMessages(updated);
          }} rows={4} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4722" data-mp="381zpe7cnxn3p" />
            </div>
          </div>)}
      </div>
    </div>;
  if (editingType) {
    return <PermisTypeBuilder typeData={editingType} onClose={() => setEditingType(null)} data-id="element-4723" data-mp="3isffm8k6cyt4" />;
  }
  return <div className="flex gap-6" data-id="element-4724" data-mp="3nsbczoyd9rzp">
      {/* Section Switcher */}
      <div className="w-48 flex-shrink-0" data-id="element-4725" data-mp="fbowcrzl0p9h">
        <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-4726" data-mp="fcrn8zwwmrpe">
          <button onClick={() => setActiveSection('departements')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'departements' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-4727" data-mp="jdtm3fmwt1qu">
            {t('Départements', 'Departments')}
          </button>
          <button onClick={() => setActiveSection('types')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'types' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-4728" data-mp="1322qco1q8y6p">
            {t('Types de Permis', 'Permit Types')}
          </button>
          <button onClick={() => setActiveSection('processus')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'processus' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-4729" data-mp="1h6t8aizvolk3">
            {t('Processus', 'Workflows')}
          </button>
          <button onClick={() => setActiveSection('messages')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'messages' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-4730" data-mp="yeh9g68fo2ep">
            {t('Messages', 'Messages')}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-4731" data-mp="350ubo134ufv0">
        {activeSection === 'departements' && renderDepartements()}
        {activeSection === 'types' && renderTypes()}
        {activeSection === 'processus' && renderProcessus()}
        {activeSection === 'messages' && renderMessages()}
      </div>
    </div>;
}